import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';

const TAB_KEYS = ['general', 'metrics', 'receivables'];

export default function useVerificationConfigPageView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [editingIds, setEditingIds] = useState<string[]>([]);
    const [showValidationPanel, setShowValidationPanel] = useState(false);
    const [batchProcess, setBatchProcess] = useState<Record<string, { received: string; status: string; observation: string }>>({});
    const [batchStatus, setBatchStatus] = useState('');
    const [batchObservation, setBatchObservation] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [sortField, setSortField] = useState('titleNumber');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [searchTerm, setSearchTerm] = useState('');

    const tabIndex = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab') || 'general'));
    const setTabIndex = (idx: number) => setSearchParams({ tab: TAB_KEYS[idx] });

    useEffect(() => {
        if (!id) return;
        refresh();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = () => {
        if (!id) return;
        setIsLoading(true);
        collateralApi.getVerificationConfigView(id)
            .then(setData)
            .catch((e: any) => {
                if (e instanceof ApiError) {
                    if (e.status === 403) return navigate('/denied');
                    if (e.status === 404) return navigate('/not-found');
                }
                navigate('/error');
            })
            .finally(() => setIsLoading(false));
    };

    const titles = useMemo(() => {
        let list = data?.titles || [];
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter((t: any) =>
                t.titleNumber?.toLowerCase().includes(s) ||
                t.nfeKey?.toLowerCase().includes(s) ||
                t.debtorName?.toLowerCase().includes(s) ||
                t.debtorDocument?.includes(s)
            );
        }
        return [...list].sort((a: any, b: any) => {
            const va = a[sortField] ?? '';
            const vb = b[sortField] ?? '';
            const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
            return sortOrder === 'ASC' ? cmp : -cmp;
        });
    }, [data, sortField, sortOrder, searchTerm]);

    const enabledProcesses = useMemo(() => {
        if (!data?.validationConfig) return [];
        const config = data.validationConfig;
        const isEnabled = (v: unknown) => v && v !== 'DO_NOT_VALIDATE';
        const processes: string[] = [];
        if (isEnabled(config.validation_xml)) processes.push('XML');
        if (isEnabled(config.validation_contact)) processes.push('DRAWEE');
        if (isEnabled(config.validation_invoice_pdf)) processes.push('INVOICE_PDF');
        if (isEnabled(config.validation_purchase_contract)) processes.push('PURCHASE_CONTRACT');
        if (isEnabled(config.validation_receipt_stub)) processes.push('RECEIPT_STUB');
        if (isEnabled(config.validation_sefaz)) processes.push('SEFAZ');
        if (isEnabled(config.validation_accepted_bill)) processes.push('ACCEPTED_BILL');
        return processes;
    }, [data]);

    const summary = useMemo(() => {
        if (!data) return { totalAmount: 0, validatedAmount: 0, docsCompleteAmount: 0, docsIncompleteAmount: 0 };
        const totalAmount = data.totalAmount || 0;
        const validatedAmount = totalAmount * ((data.validatedPercentage || 0) / 100);
        const docsCompleteAmount = titles.filter((t: any) => t.status === 'OK').reduce((s: number, t: any) => s + t.amount, 0);
        const docsIncompleteAmount = totalAmount - docsCompleteAmount;
        return { totalAmount, validatedAmount, docsCompleteAmount, docsIncompleteAmount };
    }, [data, titles]);

    const prefillAndOpenPanel = (ids: string[]) => {
        const selected = titles.filter((t: any) => ids.includes(t.id));
        const MIXED = '__MIXED__';
        const STATUS_TO_PANEL: Record<string, string> = {
            VERIFIED: 'VALIDATED', NOT_CONFIRMED: 'REJECTED', PENDING: 'PENDING',
        };
        const prefilled: Record<string, { received: string; status: string; observation: string }> = {};
        for (const p of enabledProcesses) {
            if (p === 'DRAWEE') continue;
            const items = selected.map((t: any) => (t.processItems || []).find((pi: any) => pi.processType === p)).filter(Boolean);
            const allReceived = items.length > 0 && items.every((i: any) => i.received === true);
            const allNotReceived = items.length > 0 && items.every((i: any) => i.received === false);
            const statuses = items.map((i: any) => STATUS_TO_PANEL[i.status] || i.status).filter(Boolean);
            const allSameStatus = statuses.length > 0 && statuses.every((s: string) => s === statuses[0]);
            const observations = items.map((i: any) => i.observation || '');
            const allSameObs = items.length > 0 && observations.every((o: string) => o === observations[0]);
            prefilled[p] = {
                received: allReceived ? 'true' : allNotReceived ? 'false' : (items.length > 0 ? MIXED : ''),
                status: allSameStatus ? statuses[0] : (statuses.length > 0 ? MIXED : ''),
                observation: allSameObs ? observations[0] : MIXED,
            };
        }
        setBatchProcess(prefilled);
        const docStatuses = selected.map((t: any) => t.status).filter(Boolean);
        const allSameDoc = docStatuses.length > 0 && docStatuses.every((s: string) => s === docStatuses[0]);
        setBatchStatus(allSameDoc ? docStatuses[0] : (docStatuses.length > 0 ? MIXED : ''));
        const obs = selected.map((t: any) => t.observation || '');
        const allSameObs = selected.length > 0 && obs.every((o: string) => o === obs[0]);
        setBatchObservation(allSameObs ? obs[0] : MIXED);
        setShowValidationPanel(true);
    };

    const openBatchPanel = () => {
        setEditingIds([]);
        prefillAndOpenPanel(Array.from(selectedIds));
    };

    const openEditPanel = (itemId: string) => {
        setEditingIds([itemId]);
        prefillAndOpenPanel([itemId]);
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSaveValidation = async (result: { checks: { criteria_type: string; received?: boolean; status?: string; observation?: string }[]; receivableStatus?: string; receivableObservation?: string }) => {
        if (!id) return;
        setIsSaving(true);
        try {
            const ids = editingIds.length > 0 ? editingIds : Array.from(selectedIds);
            if (result.checks.length > 0 || result.receivableStatus || result.receivableObservation) {
                await collateralApi.registerChecks(id, ids, result.checks, result.receivableStatus, result.receivableObservation);
            }
            // Optimistic UI: update local state immediately (backend processes async)
            if (data) {
                const updatedTitles = (data.titles || []).map((t: any) => {
                    if (!ids.includes(t.id)) return t;
                    const updatedProcessItems = (t.processItems || []).map((pi: any) => {
                        const check = result.checks.find(c => c.criteria_type === pi.processType);
                        if (!check) return pi;
                        return {
                            ...pi,
                            received: check.received !== undefined ? check.received : pi.received,
                            status: check.status || pi.status,
                            observation: check.observation !== undefined ? check.observation : pi.observation,
                        };
                    });
                    return {
                        ...t,
                        processItems: updatedProcessItems,
                        status: result.receivableStatus || t.status,
                        statusFormatted: result.receivableStatus || t.statusFormatted,
                        observation: result.receivableObservation !== undefined ? result.receivableObservation : t.observation,
                    };
                });

                // Recalculate processValidations (metrics) from updated titles
                const updatedProgress: Record<string, { total: number; received: number; verified: number; pending: number }> = {};
                updatedTitles.forEach((t: any) => {
                    (t.processItems || []).forEach((pi: any) => {
                        if (!updatedProgress[pi.processType]) updatedProgress[pi.processType] = { total: 0, received: 0, verified: 0, pending: 0 };
                        const p = updatedProgress[pi.processType];
                        p.total++;
                        if (pi.received) p.received++;
                        if (pi.status === 'VERIFIED') p.verified++;
                        else if (pi.status !== 'NOT_CONFIRMED') p.pending++;
                    });
                });
                const updatedProcessValidations = (data.processValidations || []).map((pv: any) => {
                    const p = updatedProgress[pv.processType];
                    if (!p) return pv;
                    const final = p.verified + (p.total - p.verified - p.pending);
                    const status = p.total > 0 && final >= p.total ? 'DONE' : p.received > 0 || final > 0 ? (final > 0 ? 'PARTIALLY_VALIDATED' : 'IN_PROGRESS') : 'NOT_STARTED';
                    const STATUS_LABELS: Record<string, string> = { NOT_STARTED: 'Não iniciado', IN_PROGRESS: 'Em validação', PARTIALLY_VALIDATED: 'Parcialmente validada', DONE: 'Concluído' };
                    return { ...pv, status, statusFormatted: STATUS_LABELS[status] || status, totalCount: p.total, receivedCount: p.received, validatedCount: p.verified, pendingCount: p.pending };
                });

                // Recalculate guaranteeStatus
                const allTotal = Object.values(updatedProgress).reduce((s: number, p: any) => s + p.total, 0);
                const allVerified = Object.values(updatedProgress).reduce((s: number, p: any) => s + p.verified, 0);
                const allReceived = Object.values(updatedProgress).reduce((s: number, p: any) => s + p.received, 0);
                const allFinal = Object.values(updatedProgress).reduce((s: number, p: any) => s + p.verified + (p.total - p.verified - p.pending), 0);
                const gStatus = allTotal === 0 ? 'NOT_STARTED' : allFinal >= allTotal ? 'DONE' : allFinal > 0 ? 'PARTIALLY_VALIDATED' : allReceived > 0 ? 'IN_PROGRESS' : 'NOT_STARTED';
                const G_LABELS: Record<string, string> = { NOT_STARTED: 'Não iniciado', IN_PROGRESS: 'Em validação', PARTIALLY_VALIDATED: 'Parcialmente validada', DONE: 'Concluído' };

                setData({ ...data, titles: updatedTitles, processValidations: updatedProcessValidations, guaranteeStatus: gStatus, guaranteeStatusFormatted: G_LABELS[gStatus] || gStatus });
            }
            toast.success('Validação sendo processada...');
            setShowValidationPanel(false);
            setSelectedIds(new Set());
            setEditingIds([]);
        } catch {
            toast.error('Erro ao registrar validação');
        } finally {
            setIsSaving(false);
        }
    };

    const [isSyncing, setIsSyncing] = useState(false);

    const syncReceivables = useCallback(async () => {
        if (!id || !data) return;
        setIsSyncing(true);
        try {
            const operationCode = data.operationCode;
            if (!operationCode) {
                toast.error('Código da operação não encontrado');
                return;
            }
            const opData = await collateralApi.getOperationData(operationCode);
            const documents = opData?.documents || [];
            const receivables = documents.flatMap((d: any) =>
                (d.receivables || []).map((r: any) => ({
                    receivable_id: r.receivable_id,
                    nfe_key: d.document_id || '',
                    title_number: d.document_title || '',
                    receivable_number: r.receivable_number || '',
                    debtor_cpf_cnpj: d.debtor_cpf_cnpj || '',
                    debtor_name: d.debtor_name || '',
                    city: d.address_city || '',
                    state: d.address_state || '',
                    emission_date: d.emission_date || '',
                    due_date: r.due_date || '',
                    amount: r.amount || 0,
                }))
            ).filter((r: any) => r.receivable_id);

            if (receivables.length === 0) {
                toast.warning('Nenhum recebível encontrado na operação');
                return;
            }

            const raw = await collateralApi.syncReceivables(id, receivables);
            const result = {
                addedCount: raw?.addedCount ?? 0,
                addedReceivableIds: raw?.addedReceivableIds ?? [],
                removedCount: raw?.removedCount ?? 0,
                removedReceivableIds: raw?.removedReceivableIds ?? [],
                markedAsRemoved: Array.isArray(raw?.markedAsRemoved) ? raw.markedAsRemoved : [],
            };

            if (result.addedCount > 0) {
                toast.success(`${result.addedCount} recebível(is) sincronizado(s)`);
            }
            if (result.removedCount > 0) {
                toast.info(`${result.removedCount} recebível(is) removido(s)`);
            }
            if (result.markedAsRemoved.length > 0) {
                toast.warning(`${result.markedAsRemoved.length} recebível(is) removido(s) da operação mas já validado(s)`);
            }
            if (result.addedCount === 0 && result.removedCount === 0 && result.markedAsRemoved.length === 0) {
                toast.info('Recebíveis já estão sincronizados');
            }
            refresh();
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                toast.error(`Erro ao sincronizar: ${error.message}`);
            } else {
                toast.error('Erro ao sincronizar recebíveis');
            }
        } finally {
            setIsSyncing(false);
        }
    }, [id, data]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        id, navigate, isLoading, data, tabIndex, setTabIndex,
        titles, enabledProcesses, summary,
        selectedIds, setSelectedIds, editingIds,
        showValidationPanel, setShowValidationPanel,
        batchProcess, setBatchProcess, batchStatus, setBatchStatus,
        batchObservation, setBatchObservation, isSaving,
        sortField, setSortField, sortOrder, setSortOrder,
        searchTerm, setSearchTerm,
        openBatchPanel, openEditPanel, toggleSelect, handleSaveValidation, refresh,
        syncReceivables, isSyncing,
    };
}
